import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { statsApi, paymentsApi, servicesApi, dataApi, reportsApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import {
  Send,
  Plus,
  LogOut,
  Wallet,
  ArrowUpRight,
  Clock,
  Search,
  Loader2,
  Home,
  History,
  FileText,
  Download,
  CreditCard,
  Building2,
  Zap,
  Globe,
  BadgeCheck
} from 'lucide-react';

export default function DashboardPage() {
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [stats, setStats] = useState(null);
  const [services, setServices] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [topupOpen, setTopupOpen] = useState(false);
  const [topupAmount, setTopupAmount] = useState('');
  const [topupMethod, setTopupMethod] = useState('stripe');
  const [topupLoading, setTopupLoading] = useState(false);
  const [downloadingReport, setDownloadingReport] = useState(false);

  const serviceIcons = {
    swift: <Building2 className="w-5 h-5" />,
    moneygram: <Zap className="w-5 h-5" />,
    western_union: <Globe className="w-5 h-5" />,
    wire_transfer: <Send className="w-5 h-5" />,
    ach: <Wallet className="w-5 h-5" />,
    sepa: <BadgeCheck className="w-5 h-5" />,
    instant: <Zap className="w-5 h-5" />
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const [statsRes, servicesRes, currenciesRes] = await Promise.all([
        statsApi.getOverview(),
        servicesApi.getAll(),
        dataApi.getCurrencies()
      ]);
      setStats(statsRes.data);
      setServices(servicesRes.data?.services || []);
      setCurrencies(currenciesRes.data || []);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const checkPaymentSuccess = async () => {
    const params = new URLSearchParams(location.search);
    const sessionId = params.get('session_id');
    const method = params.get('method');
    const paymentId = params.get('paymentId');
    const payerId = params.get('PayerID');

    if (method === 'paypal' && paymentId && payerId) {
      try {
        const res = await paymentsApi.executePaypal(paymentId, payerId);
        if (res.data.status === 'success') {
          toast.success('Payment successful! Balance updated.');
          await refreshUser();
          await fetchData();
        }
      } catch (error) {
        console.error('PayPal execution error:', error);
      }
      navigate('/dashboard', { replace: true });
    } else if (sessionId) {
      try {
        const res = await paymentsApi.getStatus(sessionId);
        if (res.data.payment_status === 'paid') {
          toast.success('Payment successful! Balance updated.');
          await refreshUser();
          await fetchData();
        }
      } catch (error) {
        console.error('Payment check error:', error);
      }
      navigate('/dashboard', { replace: true });
    }
  };

  useEffect(() => {
    fetchData();
    checkPaymentSuccess();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  const handleTopup = async () => {
    if (!topupAmount || parseFloat(topupAmount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    setTopupLoading(true);
    try {
      const res = await paymentsApi.createSession({
        amount: parseFloat(topupAmount),
        currency: 'USD',
        payment_method: topupMethod,
        origin_url: window.location.origin
      });
      
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create payment session');
    } finally {
      setTopupLoading(false);
    }
  };

  const handleDownloadReport = async () => {
    setDownloadingReport(true);
    try {
      const response = await reportsApi.downloadPdf();
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `MoneyMove_Report_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Report downloaded successfully');
    } catch (error) {
      toast.error('Failed to download report');
    } finally {
      setDownloadingReport(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-900/20 via-green-900/10 to-teal-900/20 flex items-center justify-center">
        <motion.div
          initial={{ scale: 0.5, opacity: 0, rotate: -180 }}
          animate={{ scale: 1, opacity: 1, rotate: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="text-center"
        >
          <motion.div
            animate={{ 
              boxShadow: [
                '0 0 20px rgba(16, 185, 129, 0.3)',
                '0 0 40px rgba(16, 185, 129, 0.6)',
                '0 0 20px rgba(16, 185, 129, 0.3)'
              ]
            }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            className="w-20 h-20 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center mx-auto mb-4"
          >
            <Send className="w-10 h-10 text-white" />
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            <Loader2 className="w-8 h-8 text-emerald-500 animate-spin mx-auto" />
          </motion.div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-900/20 via-green-900/10 to-teal-900/20" data-testid="dashboard-page">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-full w-64 bg-slate-900/80 backdrop-blur-sm border-r border-emerald-500/20 p-6 hidden lg:flex flex-col shadow-lg">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg">
            <Send className="w-5 h-5 text-white" />
          </div>
          <span className="text-xl font-bold text-emerald-100 font-manrope">MoneyMove</span>
        </div>

        <nav className="flex-1 space-y-2">
          <Link to="/dashboard" className="flex items-center gap-3 px-4 py-3 rounded-lg bg-emerald-500/10 text-emerald-100 border border-emerald-500/30 hover:bg-emerald-500/20 transition-colors">
            <Home className="w-5 h-5 text-emerald-400" />
            <span className="font-medium">Dashboard</span>
          </Link>
          <Link to="/send" className="flex items-center gap-3 px-4 py-3 rounded-lg text-emerald-200 hover:bg-emerald-500/10 hover:text-emerald-50 transition-colors">
            <Send className="w-5 h-5 text-emerald-400" />
            <span className="font-medium">Send Money</span>
          </Link>
          <Link to="/transactions" className="flex items-center gap-3 px-4 py-3 rounded-lg text-emerald-200 hover:bg-emerald-500/10 hover:text-emerald-50 transition-colors">
            <History className="w-5 h-5 text-emerald-400" />
            <span className="font-medium">Transactions</span>
          </Link>
          <Link to="/track" className="flex items-center gap-3 px-4 py-3 rounded-lg text-emerald-200 hover:bg-emerald-500/10 hover:text-emerald-50 transition-colors">
            <Search className="w-5 h-5 text-emerald-400" />
            <span className="font-medium">Track Transfer</span>
          </Link>
        </nav>

        <div className="pt-4 border-t border-emerald-500/20">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center text-emerald-400 font-bold">
              {user?.full_name?.[0]}
            </div>
            <div>
              <p className="text-emerald-100 text-sm font-medium">{user?.full_name}</p>
              <p className="text-emerald-300/70 text-xs">{user?.email}</p>
            </div>
          </div>
          <Button 
            variant="ghost" 
            className="w-full justify-start text-emerald-300 hover:text-emerald-100"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2 text-emerald-400" />
            Sign Out
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 p-6 lg:p-8">
        {/* Mobile Header */}
        <div className="lg:hidden flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg">
              <Send className="w-4 h-4 text-white" />
            </div>
            <span className="text-lg font-bold text-emerald-100 font-manrope">MoneyMove</span>
          </div>
          <Button variant="ghost" size="icon" onClick={handleLogout}>
            <LogOut className="w-5 h-5 text-emerald-400" />
          </Button>
        </div>

        {/* Welcome */}
        <div className="mb-8">
          <h1 className="text-2xl lg:text-3xl font-bold text-emerald-50 mb-2 font-manrope">
            Welcome back, {user?.full_name?.split(' ')[0]}
          </h1>
          <p className="text-emerald-300/70">Manage your transfers and account</p>
        </div>

        {/* Balance Card */}
        <div className="bg-slate-900/60 backdrop-blur-sm border border-emerald-500/30 rounded-2xl p-6 mb-8 relative overflow-hidden shadow-xl">
          <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/10 rounded-full blur-[80px]" />
          <div className="relative z-10">
            <div className="flex items-start justify-between mb-6">
              <div>
                <p className="text-emerald-300/70 text-sm mb-2">Available Balance</p>
                <p className="text-4xl lg:text-5xl font-bold text-emerald-50 amount-display font-manrope" data-testid="balance-amount">
                  ${user?.balance?.toFixed(2) || '0.00'}
                </p>
                <p className="text-emerald-400 text-sm mt-2">{user?.currency || 'USD'}</p>
              </div>
              <div className="w-14 h-14 rounded-full bg-emerald-500/20 flex items-center justify-center shadow-md">
                <Wallet className="w-7 h-7 text-emerald-400" />
              </div>
            </div>
            
            <div className="flex gap-3">
              {/* Topup Dialog */}
              <Dialog open={topupOpen} onOpenChange={setTopupOpen}>
                <DialogTrigger asChild>
                  <Button className="flex-1 bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-bold hover:from-emerald-600 hover:to-teal-700 shadow-lg" data-testid="topup-btn">
                    <Plus className="w-5 h-5 mr-2" />
                    Add Funds
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-slate-900/90 backdrop-blur-sm border-emerald-500/30">
                  <DialogHeader>
                    <DialogTitle className="text-emerald-50 text-xl font-manrope">Add Funds</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 pt-4">
                    <div className="space-y-2">
                      <Label className="text-emerald-300">Amount (USD)</Label>
                      <Input
                        type="number"
                        placeholder="100.00"
                        value={topupAmount}
                        onChange={(e) => setTopupAmount(e.target.value)}
                        className="bg-slate-800/50 border-emerald-500/30 text-emerald-50 h-12 focus:border-emerald-400 focus:ring-emerald-400/20"
                        data-testid="topup-amount-input"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="text-emerald-300">Payment Method</Label>
                      <div className="grid grid-cols-2 gap-3">
                        <button
                          onClick={() => setTopupMethod('stripe')}
                          className={`p-4 rounded-lg border transition-all ${
                            topupMethod === 'stripe' 
                              ? 'border-emerald-500 bg-emerald-500/10' 
                              : 'border-emerald-500/20 hover:border-emerald-400/40'
                          }`}
                          data-testid="stripe-method-btn"
                        >
                          <CreditCard className={`w-6 h-6 mx-auto mb-2 ${topupMethod === 'stripe' ? 'text-emerald-400' : 'text-emerald-600'}`} />
                          <p className={`text-sm font-medium ${topupMethod === 'stripe' ? 'text-emerald-50' : 'text-emerald-400'}`}>Card / Stripe</p>
                        </button>
                        <button
                          onClick={() => setTopupMethod('paypal')}
                          className={`p-6 h-24 rounded-lg border transition-all ${
                            topupMethod === 'paypal' 
                              ? 'border-emerald-500 bg-emerald-500/10' 
                              : 'border-emerald-500/20 hover:border-emerald-400/40'
                          }`}
                          data-testid="paypal-method-btn"
                        >
                          <Wallet className={`w-6 h-6 mx-auto mb-2 ${topupMethod === 'paypal' ? 'text-emerald-400' : 'text-emerald-600'}`} />
                          <p className={`text-sm font-medium ${topupMethod === 'paypal' ? 'text-emerald-50' : 'text-emerald-400'}`}>PayPal</p>
                        </button>
                      </div>
                    </div>
                    
                    <Button
                      onClick={handleTopup}
                      disabled={topupLoading}
                      className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-bold h-12 shadow-lg"
                      data-testid="topup-submit-btn"
                    >
                      {topupLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : `Pay with ${topupMethod === 'stripe' ? 'Stripe' : 'PayPal'}`}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>

              <Link to="/send" className="flex-1">
                <Button className="w-full bg-slate-800/50 text-emerald-50 font-bold hover:bg-slate-700/50 border border-emerald-500/20 shadow-sm" data-testid="send-btn">
                  <Send className="w-5 h-5 mr-2 text-emerald-400" />
                  Send Money
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid md:grid-cols-3 gap-4 mb-8">
          <div className="bg-slate-900/40 backdrop-blur-sm border border-emerald-500/20 rounded-xl p-5 shadow-lg">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center shadow-md">
                <ArrowUpRight className="w-5 h-5 text-emerald-400" />
              </div>
              <span className="text-emerald-300/70 text-sm">Total Sent</span>
            </div>
            <p className="text-2xl font-bold text-emerald-50 amount-display font-manrope" data-testid="total-sent">
              ${stats?.total_sent?.toFixed(2) || '0.00'}
            </p>
            <p className="text-emerald-400/60 text-sm mt-1">{stats?.transfer_count || 0} transfers</p>
          </div>
          
          <div className="bg-slate-900/40 backdrop-blur-sm border border-emerald-500/20 rounded-xl p-5 shadow-lg">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center shadow-md">
                <Clock className="w-5 h-5 text-amber-400" />
              </div>
              <span className="text-emerald-300/70 text-sm">Total Fees</span>
            </div>
            <p className="text-2xl font-bold text-emerald-50 amount-display font-manrope" data-testid="total-fees">
              ${stats?.total_fees?.toFixed(2) || '0.00'}
            </p>
            <p className="text-emerald-400/60 text-sm mt-1">All time</p>
          </div>

          <div className="bg-slate-900/40 backdrop-blur-sm border border-emerald-500/20 rounded-xl p-5 shadow-lg">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-cyan-500/20 flex items-center justify-center shadow-md">
                <FileText className="w-5 h-5 text-cyan-400" />
              </div>
              <span className="text-emerald-300/70 text-sm">Reports</span>
            </div>
            <Button 
              onClick={handleDownloadReport}
              disabled={downloadingReport}
              variant="ghost" 
              className="text-emerald-400 hover:text-emerald-300 p-0 h-auto"
              data-testid="download-report-btn"
            >
              {downloadingReport ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2 text-emerald-400" />
              ) : (
                <Download className="w-4 h-4 mr-2 text-emerald-400" />
              )}
              Download PDF
            </Button>
          </div>
        </div>

        {/* Transfer Services */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-emerald-50 font-manrope">Transfer Services</h2>
            <Link to="/send" className="text-emerald-400 text-sm hover:text-emerald-300 hover:underline">
              Start Transfer →
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {(services || []).slice(0, 6).map((service, index) => (
              <motion.div
                key={service.code}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Link 
                  to={`/send?service=${service.code.toLowerCase()}`}
                  className="bg-slate-900/40 backdrop-blur-sm border border-emerald-500/20 rounded-xl p-4 text-center hover:border-emerald-500/40 transition-colors block shadow-sm"
                >
                  <div className="w-10 h-10 rounded-lg bg-emerald-500/10 text-emerald-400 flex items-center justify-center mx-auto mb-2 shadow-md">
                    {serviceIcons[service.code.toLowerCase()] || <Send className="w-5 h-5" />}
                  </div>
                  <p className="text-emerald-50 text-sm font-medium">{service.name}</p>
                  <p className="text-emerald-400/60 text-xs mt-1">
                    {service.min_days === service.max_days && service.min_days === 0 
                      ? 'Instant' 
                      : `${service.min_days}-${service.max_days} days`}
                  </p>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Recent Transactions */}
        <div className="bg-slate-900/40 backdrop-blur-sm border border-emerald-500/20 rounded-xl shadow-lg">
          <div className="flex items-center justify-between p-5 border-b border-emerald-500/20">
            <h2 className="text-lg font-bold text-emerald-50 font-manrope">Recent Transfers</h2>
            <Link to="/transactions" className="text-emerald-400 text-sm hover:text-emerald-300 hover:underline" data-testid="view-all-transactions">
              View All
            </Link>
          </div>
          
          <div className="divide-y divide-emerald-500/10">
            {stats?.recent_transfers?.length > 0 ? (
              stats.recent_transfers.map((tx) => (
                <Link 
                  key={tx.id} 
                  to={`/track?number=${tx.tracking_number}`}
                  className="p-5 flex items-center justify-between hover:bg-emerald-500/5 transition-colors block"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center text-emerald-400 shadow-md">
                      {serviceIcons[tx.service_type] || <Send className="w-5 h-5" />}
                    </div>
                    <div>
                      <p className="text-emerald-50 font-medium">{tx.recipient_name}</p>
                      <p className="text-emerald-400/60 text-sm">{tx.service_name} • {tx.recipient_country}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-emerald-50 font-bold amount-display font-manrope">
                      -${tx.amount.toFixed(2)}
                    </p>
                    <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${tx.status === 'completed' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'}`}>
                      {tx.status}
                    </span>
                  </div>
                </Link>
              ))
            ) : (
              <div className="p-10 text-center text-emerald-400/60">
                No transfers yet. Start by sending money!
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Mobile Navigation */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-slate-900/80 backdrop-blur-sm border-t border-emerald-500/20 p-4 shadow-lg">
        <div className="flex justify-around">
          <Link to="/dashboard" className="flex flex-col items-center text-emerald-400">
            <Home className="w-6 h-6" />
            <span className="text-xs mt-1 text-emerald-400 font-medium">Home</span>
          </Link>
          <Link to="/send" className="flex flex-col items-center text-emerald-600">
            <Send className="w-6 h-6" />
            <span className="text-xs mt-1 text-emerald-600 font-medium">Send</span>
          </Link>
          <Link to="/transactions" className="flex flex-col items-center text-emerald-600">
            <History className="w-6 h-6" />
            <span className="text-xs mt-1 text-emerald-600 font-medium">History</span>
          </Link>
          <Link to="/track" className="flex flex-col items-center text-emerald-600">
            <Search className="w-6 h-6" />
            <span className="text-xs mt-1 text-emerald-600 font-medium">Track</span>
          </Link>
        </div>
      </nav>
    </div>
  );
}
